<!-- eslint-disable vue/no-mutating-props -->
<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @click="emit('hideModal')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center lg:items-center lg:p-0"
          @click="emit('hideModal')">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
            enter-to="opacity-100 translate-y-0 lg:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 lg:scale-100"
            leave-to="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all lg:my-8 w-full lg:max-w-lg lg:p-6">
              <div class="text-center">
                <span class="font-bold">{{ $t("Contact") }}</span>
                <div class="absolute right-0 top-0 pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    @click="emit('hideModal')">
                    <span class="sr-only">{{ $t("Close") }}</span>
                    <XMarkIcon class="size-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <hr class="my-6" />
              <div v-if="contactFormSuccess">
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center lg:mt-5">
                  <DialogTitle as="h3" class="font-semibold text-gray-900">{{ $t("Thank you!") }}</DialogTitle>
                  <div class="mt-2">
                    <p class="text-base text-gray-500">{{ $t("We will contact you as soon as possible!") }}</p>
                  </div>
                </div>
              </div>
              <form v-else class="max-w-lg" @submit.prevent="handleSubmit">
                <div class="flex flex-wrap -mx-3 mb-6">
                  <div class="w-full lg:w-2/4 px-3 mb-6 lg:mb-0">
                    <label
                      class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2"
                      for="grid-first-name">
                      {{ $t("First name") }}*
                    </label>
                    <input
                      id="grid-first-name"
                      v-model="request.customer_first_name"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="text"
                      :aria-label="$t('First name')"
                      placeholder=""
                      required />
                  </div>
                  <div class="w-full lg:w-2/4 px-3">
                    <label
                      class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2"
                      for="grid-last-name">
                      {{ $t("Last name") }}*
                    </label>
                    <input
                      id="grid-last-name"
                      v-model="request.customer_last_name"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="text"
                      :aria-label="$t('Last name')"
                      placeholder=""
                      required />
                  </div>
                </div>
                <div class="flex flex-wrap -mx-3 mb-6">
                  <div class="w-full px-3">
                    <label class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2" for="grid-email">
                      {{ $t("Email") }}*
                    </label>
                    <input
                      id="grid-email"
                      v-model="request.customer_email"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="email"
                      placeholder=""
                      required />
                  </div>
                </div>
                <div class="flex flex-wrap -mx-3 mb-6">
                  <div class="w-full px-3">
                    <label class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2" for="grid-phone">
                      {{ $t("Phone number") }}
                    </label>
                    <input
                      id="grid-phone"
                      v-model="request.customer_phone"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="tel"
                      placeholder="" />
                  </div>
                </div>
                <div class="flex flex-wrap -mx-3 mb-6">
                  <div class="w-full px-3">
                    <label
                      class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2"
                      for="grid-subject">
                      {{ $t("Subject") }}*
                    </label>
                    <input
                      id="grid-subject"
                      v-model="request.subject"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="text"
                      placeholder=""
                      required />
                  </div>
                </div>
                <div class="flex flex-wrap -mx-3 mb-6">
                  <div class="w-full px-3">
                    <label
                      class="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2"
                      for="grid-comment">
                      {{ $t("Comment") }}*
                    </label>
                    <textarea
                      id="grid-comment"
                      v-model="request.customer_comment"
                      class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="text"
                      placeholder=""
                      required>
                    </textarea>
                  </div>
                </div>
                <div class="mt-5 lg:mt-6">
                  <button
                    type="submit"
                    class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 font-semibold shadow-sm hover:shadow active:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary text-btn-forecolor">
                    {{ $t("Send request") }}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { CheckIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { onMounted, ref, watch } from "vue";
import brandconfig from "@/brandconfig";
import { useEventStore } from "@/store/event.js";
import { useUserStore } from "@/store/user.js";
import { useBookingsStore } from "@/store/bookings.js";

const eventStore = useEventStore();
const userStore = useUserStore();
const contactFormSuccess = ref(false);

const props = defineProps(["open"]);
const emit = defineEmits(["hideModal"]);

watch(
  () => props.open,
  (n) => {
    if (!n) {
      emit("hideModal");
    }
  }
);

onMounted(() => {
  // Pre-fill email if user is logged in (B2B context)
  if (userStore?.username) {
    request.value.customer_email = userStore.username;
  }
});

const request = ref({
  customer_first_name: null,
  customer_last_name: null,
  customer_email: null,
  customer_phone: null,
  subject: null,
  customer_comment: "",
  recipient: brandconfig.BRAND == "xs2event" ? `support@xs2event.com` : `support+${brandconfig.BRAND}@xs2event.com`,
});

const bookingStore = useBookingsStore();

const handleSubmit = () => {
  console.log(userStore);
  const payload = {
    template_name: "contactform_portal.html",
    context_data: {
      booking_code: bookingStore?.bookingorders[0].booking_code ? bookingStore?.bookingorders[0].booking_code : "",
      customer_name: `${request.value.customer_first_name} ${request.value.customer_last_name}`,
      customer_email: request.value.customer_email,
      customer_phone: request.value.customer_phone ? request.value.customer_phone : "",
      event_name: "",
      subject: request.value.subject,
      customer_comment: request.value.customer_comment,
      distributor_name: brandconfig.BRAND,
      datetime: new Date().toLocaleString(),
      recipient: request.value.recipient,
    },
  };
  eventStore.requestMail(payload).then(() => {
    contactFormSuccess.value = true;
  });
};
</script>
